import React from 'react';
import {
    Body1,
    Subtitle1,
    Title3,
    Divider,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import AppDrawer from '../Common/AppDrawer';
import TabbedDrawerContent from '../Common/TabbedDrawerContent';

interface Section {
    title: string;
    description: React.ReactNode;
    correct?: React.ReactNode;
    wrong?: React.ReactNode;
}

interface TroubleshootingDrawerProps {
    open: boolean;
    toggleDrawer: (open: boolean) => void;
}

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        paddingTop: '32px',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px 32px',
        '& a': {
            color: '#4CC48A',
            textDecoration: 'underline',
            '&:hover': {
                color: '#7EDAA9',
            },
            '&:active': {
                color: '#3AA371',
            },
        },
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    exampleBlock: {
        backgroundColor: tokens.colorNeutralBackground3,
        padding: '12px 12px 12px 16px',
        position: 'relative',
        marginTop: '8px',
        borderRadius: '4px',
    },
    exampleAccent: {
        position: 'absolute',
        left: '0',
        top: '0',
        bottom: '0',
        width: '4px',
        backgroundColor: tokens.colorBrandForeground1,
        borderRadius: '4px 0 0 4px',
    },
    exampleContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        marginLeft: '8px',
    },
});

const zeroCountSections: Section[] = [
    {
        title: "Why did I get a Zero user count?",
        description: (
            <>
                GSC is in beta, and as a result, you may run into situations where GSC returns a count of 0 that seems wrong to you.
                There are many reasons that can happen, and the list below will help you determine what went wrong.
                Also, we are always available to help you diagnose this together if that's more convenient! Drop us a message anytime on our
                <a href="https://teams.microsoft.com/l/team/19%3AulE_fOmY8v6tJdC_8I5zEnFzzcGRmTl_M8sjXb-HbgA1%40thread.tacv2/conversations?groupId=cc086976-faf9-403c-b6cd-f1724a07bf84&tenantId=72f988bf-86f1-41af-91ab-2d7cd011db47"
                    target="_blank" rel="noopener noreferrer"> Teams community channel</a>.
            </>
        ),
    },
    {
        title: "\u{1F4C5} Unsupported date range selected for Play/App usage",
        description: "Play history (for example, players who played Fortnite) and app usage (for example, players who used the Garrison app) are only available for monthly or yearly range, at the moment.",
        wrong: "\u274C How many people have played both Call of Duty and Fortnite in the last 30 days?",
        correct: "\u2705 How many people have played both Call of Duty and Fortnite in October 2024?"
    },
    {
        title: "\u23F1\uFE0F Data does not exist due to GSC's data latency of 3\u20135 days",
        description: "",
        wrong: "\u274C Users who created an account in the last 5 days",
        correct: "\u274C Users who purchased Hogwarts Legacy yesterday"
    },
    {
        title: "\u{1F5A5}\uFE0F Game title not available on the Platform you selected",
        description: "Probably, you selected a title available only on Console but asked how many played it on PC. Remember to check the Platform supported while picking a Title!",
        wrong: "\u274C Users who played Minecraft (TitleId: 1828326430) on PC leads to a valid zero response, as this title is available only for play on Console.",
        correct: "\u2705 Users who played Minecraft (TitleId: 1828326430) on Console"
    },
    {
        title: "\u2699\uFE0F Zero users meet the audience size you defined",
        description: "The audience size you wish to generate might have too many conditions, so no users match all of them.",
        wrong: "\u274C Users who have a PC Game Pass subscription, used the Garrison app, played Diablo IV, and purchased Diablo: Vessel of Hatred.",
        correct: "\u2705 Try this: Simplify your search. For example, look for users who have a PC Game Pass subscription, used the Garrison app, and played Diablo IV."
    },
    {
        title: "\u{1F512} Access restrictions may have prevented the query",
        correct: "\u274C You might not have the required permissions to access certain user data or titles.",
        description: "If access restrictions might be causing zero counts, please contact Casie Schuller/Shubham Roy on Teams to verify your permissions."
    },
    {
        title: "\u{1F6E0}\uFE0F Technical error at GSC's end",
        description: (
            <>
                If none of the above seems to be the case, it's likely due to an error in generating the code --
                we all love our copilots, but they do make mistakes sometimes! And as we said earlier, we are always available to help you diagnose this together. Drop us a message anytime on our
                <a href="https://teams.microsoft.com/l/team/19%3AulE_fOmY8v6tJdC_8I5zEnFzzcGRmTl_M8sjXb-HbgA1%40thread.tacv2/conversations?groupId=cc086976-faf9-403c-b6cd-f1724a07bf84&tenantId=72f988bf-86f1-41af-91ab-2d7cd011db47"
                    target="_blank" rel="noopener noreferrer"> Teams community channel</a>.
            </>
        ),
    }
];

const ZeroCountContent: React.FC<{ classes: ReturnType<typeof useStyles> }> = ({ classes }) => (
    <div className={classes.content}>
        {zeroCountSections.map((section, index) => (
            <React.Fragment key={index}>
                <div className={classes.section}>
                    {index === 0 ? (
                        <Title3>{section.title}</Title3>
                    ) : (
                        <Subtitle1>{section.title}</Subtitle1>
                    )}
                    {section.description && <Body1>{section.description}</Body1>}
                    {(section.wrong || section.correct) && (
                        <div className={classes.exampleBlock}>
                            <div className={classes.exampleAccent} />
                            <div className={classes.exampleContent}>
                                {section.wrong && <Body1>{section.wrong}</Body1>}
                                {section.correct && <Body1>{section.correct}</Body1>}
                            </div>
                        </div>
                    )}
                </div>
                {index < zeroCountSections.length - 1 && <Divider />}
            </React.Fragment>
        ))}
    </div>
);

const BestPracticesContent: React.FC<{ classes: ReturnType<typeof useStyles> }> = ({ classes }) => (
    <div className={classes.content}>
        <div className={classes.section}>
            <Title3>Best Practices</Title3>
            <Body1>
                Follow these tips to get the most accurate and useful results from GSC.
            </Body1>
        </div>
        <Divider />
        <div className={classes.section}>
            <Subtitle1>🎯 Be specific with your queries</Subtitle1>
            <Body1>
                Include specific title names, date ranges, and platforms to help GSC generate the most accurate audience segments.
            </Body1>
            <div className={classes.exampleBlock}>
                <div className={classes.exampleAccent} />
                <div className={classes.exampleContent}>
                    <Body1>{"\u274C"} Users who played a game recently</Body1>
                    <Body1>{"\u2705"} Users who played Fortnite on Console in March 2025</Body1>
                </div>
            </div>
        </div>
        <Divider />
        <div className={classes.section}>
            <Subtitle1>📅 Use supported date formats</Subtitle1>
            <Body1>
                Prefer named months or specific dates rather than relative ranges like "last 30 days" for play and app usage queries.
            </Body1>
        </div>
        <Divider />
        <div className={classes.section}>
            <Subtitle1>🔍 Start broad, then narrow</Subtitle1>
            <Body1>
                If you're unsure about your audience size, start with fewer conditions and add more incrementally to see how each filter affects the count.
            </Body1>
        </div>
        <Divider />
        <div className={classes.section}>
            <Subtitle1>📝 Review the generated script</Subtitle1>
            <Body1>
                Use the "Script & Logic Used" section to verify that GSC interpreted your query correctly. If something looks off, try rephrasing your request.
            </Body1>
        </div>
    </div>
);

const TroubleshootingDrawer: React.FC<TroubleshootingDrawerProps> = ({ open, toggleDrawer }) => {
    const classes = useStyles();

    const tabs = [
        { value: 'zero-count', label: 'Zero Counts', content: <ZeroCountContent classes={classes} /> },
        { value: 'best-practices', label: 'Best Practices', content: <BestPracticesContent classes={classes} /> },
    ];

    return (
        <AppDrawer
            open={open}
            onClose={() => toggleDrawer(false)}
            size="large"
        >
            <div className={classes.container}>
                <TabbedDrawerContent tabs={tabs} defaultTab="zero-count" />
            </div>
        </AppDrawer>
    );
};

export default TroubleshootingDrawer;
